'use strict';


import { MathUtils, Spherical, Vector3 } from 'three';
import { BaseElement } from '../../interfaces/BaseElement';
import { CSS2DObject } from '../../../libs/CSS2DRenderer';
import { GSAPAnimationMixer, settingsData, threeJsVariables, UIRoot } from '../../../settings';
import { calculateDeltaFromVec3, clamp } from '../../../functions';
import { Lerp } from '../../projectTime/Lerp';
import { getCurrentCameraDistance } from '../../controls/orbitControls';
import gsap from "gsap";
import { CameraCollision } from '../CameraCollision';
import { changeLangToCurrent } from '../../components/langSwitcher/langSwitcher';
import { ViewMode } from '../../../common';
import { OrbitDayAndNight } from '../../liveAnimations/DayNight/OrbitDayAndNight';

export class PointView {
	constructor (data) {
		this.position = new Vector3().fromArray(data.position);
		this.direction = new Vector3().fromArray(data.direction);
		this.theta = MathUtils.degToRad(data.theta);
		this.phi = MathUtils.degToRad(data.phi);
		this.time = data.hasOwnProperty('time') ? data.time : '';
		this.cachedOrbitControls = new Map();
		this.cachedCamera = new Map();
		this.cachedTrackball = new Map();
		this.cachedLabels = new Map();
		this.currentTimeCache = null;
		this.label = null;
		// this.btn = null;
		this.objectType = 'PointView';
	}
	
	createLabel() {
		const container = new BaseElement()
			.addClass('point-view-label')
			.DOMElement;
		
		// new BaseElement()
		// 	.addClass('point-view-label__content')
		// 	.appendTo(container);
		
		this.label = new CSS2DObject(container);
		this.label.userData.zoom = 1;
		this.label.name =`layer-poi__orbit-360`;
		// this.label.element.addEventListener('click',this.viewIn.bind(this));
		this.label.position.copy(this.position);
		
		
		// this.btn = new BaseElement('button')
		// 	.addClass('control-button','point-view__back-btn','language-string')
		// 	.addAttribute('data-dictionary','Back to orbit')
		// 	.innerHtml(changeLangToCurrent('Back to orbit'))
		// 	.event('click',this.viewOut.bind(this))
		// 	.DOMElement;
		
		return this.label;
	}
	
	visibilityToggle(state,btnState) {
		this.label.visible = state;
		this.controlsToggle(btnState !== undefined ? btnState : !state);
	}
	
	setPointViewControls(state) {
		if (state) {
			threeJsVariables.orbitControls.rotateSpeed = 0.25;
			threeJsVariables.orbitControls.reverseControls = true;
			threeJsVariables.trackballControls.noZoom = true;
		} else {
			threeJsVariables.orbitControls.rotateSpeed = 1;
			threeJsVariables.orbitControls.reverseControls = false;
			threeJsVariables.trackballControls.noZoom = false;
		}
	}
	
	show(state) {
		if (state) {
			this.label.element.classList.remove('--hide-point-view');
			this.label.element.classList.add('--show-point-view');
		} else {
			this.label.element.classList.add('--hide-point-view');
			this.label.element.classList.remove('--show-point-view');
		}
	}
	
	controlsToggle(state) {
		if (settingsData.activeViewMode === ViewMode.Cylinder || !state) {
			this.setPointViewControls(false);
		} else {
			this.setPointViewControls(true);
		}
	}
	
	#calculateDelta(from,to) {
		const tempD = to - from;
		let delta;
		
		if (tempD < 0 && tempD < -Math.PI) {
			delta = tempD + 2 * Math.PI;
		} else if (tempD > 0 && tempD > Math.PI) {
			delta = tempD - 2 * Math.PI;
		} else {
			delta = tempD;
		}
		
		return delta;
	}
	
	#saveSettings() {
		const trackballControls = threeJsVariables.trackballControls;
		const controls = threeJsVariables.orbitControls;
		const camera = threeJsVariables.perspectiveCamera;
		
		this.cachedOrbitControls.clear();
		this.cachedOrbitControls.set('maxSphere',controls.maxSphere);
		this.cachedOrbitControls.set('minPolarAngle',controls.minPolarAngle);
		this.cachedOrbitControls.set('maxPolarAngle',controls.maxPolarAngle);
		this.cachedOrbitControls.set('minAzimuthAngle',controls.minAzimuthAngle);
		this.cachedOrbitControls.set('maxAzimuthAngle',controls.maxAzimuthAngle);
		this.cachedOrbitControls.set('minPan',controls.minPan);
		this.cachedOrbitControls.set('maxPan',controls.maxPan);
		this.cachedOrbitControls.set('rotateSpeed',controls.rotateSpeed);
		this.cachedOrbitControls.set('target',controls.target.clone());
		
		this.cachedCamera.clear();
		this.cachedCamera.set('position',camera.position.clone());
		
		this.cachedTrackball.clear();
		this.cachedTrackball.set('minDistance',trackballControls.minDistance);
		this.cachedTrackball.set('maxDistance',trackballControls.maxDistance);
		
		this.cachedLabels.clear();
		this.cachedLabels.set('resize',config.threejs.label.resize);
		
		this.currentTimeCache = null;
		
		if (OrbitDayAndNight.isActiveAndSelected) {
			this.currentTimeCache = Number(OrbitDayAndNight.scope.range.element.value);
		}
	}
	
	#applyPointSettings() {
		CameraCollision.prevent = true;
		const trackballControls = threeJsVariables.trackballControls;
		const controls = threeJsVariables.orbitControls;
		
		trackballControls.minDistance = 0.01;
		trackballControls.noZoom = true;
		controls.maxSphere = undefined;
		controls.maxPan = undefined;
		controls.minPan = undefined;
		controls.maxPolarAngle = Math.PI;
		controls.minPolarAngle = 0;
		controls.rotateSpeed = 0.25;
	}
	
	restoreControlsSettings() {
		const trackballControls = threeJsVariables.trackballControls;
		const controls = threeJsVariables.orbitControls;
		
		this.cachedOrbitControls.forEach((value,key) => {
			controls[key] = value;
		});
		
		this.cachedTrackball.forEach((value,key) => {
			trackballControls[key] = value;
		});
		
		if (OrbitDayAndNight.isActiveAndSelected) {
			OrbitDayAndNight.scope.range.element.value = this.currentTimeCache;
			OrbitDayAndNight.scope.updateTimesOfDay(this.currentTimeCache, true);
		}
	}
	
	#applyAzimuthPolarLimits() {
		const controls = threeJsVariables.orbitControls;
		const azimuth = controls.getAzimuthalAngle();
		const polar = controls.getPolarAngle();
		
		if (this.theta === Math.PI * 2) {
			controls.minAzimuthAngle = -Infinity;
			controls.maxAzimuthAngle = Infinity;
		} else {
			controls.minAzimuthAngle = azimuth + this.#calculateDelta(azimuth,azimuth - this.theta / 2);
			controls.maxAzimuthAngle = azimuth + this.#calculateDelta(azimuth,azimuth + this.theta / 2);
		}
		
		let min = clamp(polar + this.#calculateDelta(polar,polar - this.phi / 2),0,Math.PI);
		let max = clamp(polar + this.#calculateDelta(polar,polar + this.phi / 2),0,Math.PI);
		
		if ((min === 0 && max === 0) || (min === Math.PI && max === Math.PI)) {
			min = 0;
			max = Math.PI;
		}
		
		controls.minPolarAngle = min;
		controls.maxPolarAngle = max;
	}
	
	viewIn() {
		this.#saveSettings();
		this.#applyPointSettings();
		this.visibilityToggle(false,false);
		config.threejs.label.resize = false;
		
		const trackballControls = threeJsVariables.trackballControls;
		const controls = threeJsVariables.orbitControls;
		const camera = threeJsVariables.perspectiveCamera;
		const direction = new Vector3().subVectors(this.position,this.direction).normalize();
		const delta = calculateDeltaFromVec3(this.position,this.direction);
		const spherical = new Spherical().setFromVector3(direction);
		
		const currentDelta = controls.getAzimuthalAngle();
		const currentPolarAngle = controls.getPolarAngle();
		const thetaLerp = new Lerp(currentDelta,currentDelta + delta);
		const phiLerp = new Lerp(currentPolarAngle,spherical.phi);
		const distanceLerp = new Lerp(getCurrentCameraDistance(),trackballControls.minDistance);
		const transition = threeJsVariables.composer.getPass('map');
		transition.scale = 0;
		transition.opacity = 1;
		transition.active = true;
		
		gsap.to(controls.target,{
			x: this.position.x,
			y: this.position.y,
			z: this.position.z,
			duration: GSAPAnimationMixer.duration,
			ease: GSAPAnimationMixer.ease
		});
		
		const cameraProgress = {
			value: 0
		};
		
		const dir = new Vector3();
		
		gsap.to(cameraProgress,{
			value: 1,
			duration: GSAPAnimationMixer.duration,
			ease: GSAPAnimationMixer.ease,
			onUpdate: () => {
				if (cameraProgress.value < 0.5) {
					transition.opacity = clamp(1 - cameraProgress.value,0.5,1);
				} else {
					transition.opacity = clamp(cameraProgress.value,0.5,1);
				}
				controls.setAzimuthAngle(thetaLerp.lerp(cameraProgress.value));
				controls.setPolarAngle(phiLerp.lerp(cameraProgress.value));
				dir.subVectors(camera.position,controls.target);
				dir.normalize().setLength(distanceLerp.lerp(cameraProgress.value)).add(controls.target);
				camera.position.copy(dir);
			},
			onComplete: () => {
				transition.active = false;
				this.#applyAzimuthPolarLimits();
				this.controlsToggle(true);
			}
		});
		
		if (OrbitDayAndNight.isActiveAndSelected && this.time.length) {
			const [hours,minutes] = this.time.split(':');
			const time = {value: Number(OrbitDayAndNight.scope.range.element.value)};
			
			gsap.to(time,{
				value: new Date(OrbitDayAndNight.scope.currentTime).setHours(hours,minutes),
				ease: GSAPAnimationMixer.ease,
				duration: GSAPAnimationMixer.duration,
				onUpdate: () => {
					OrbitDayAndNight.scope.range.element.value = time.value;
					OrbitDayAndNight.scope.updateTimesOfDay(time.value, true);
				}
			});
		}
		
	}
	
	viewOut() {
		this.controlsToggle(false);
		this.visibilityToggle(true);
		const trackballControls = threeJsVariables.trackballControls;
		const controls = threeJsVariables.orbitControls;
		const camera = threeJsVariables.perspectiveCamera;
		const transition = threeJsVariables.composer.getPass('map');
		
		trackballControls.noZoom = false;
		controls.maxPolarAngle = Math.PI;
		controls.minPolarAngle = 0;
		controls.rotateSpeed = 1;
		controls.maxAzimuthAngle = Infinity;
		controls.minAzimuthAngle = -Infinity;
		
		const target = this.cachedOrbitControls.get('target');
		const position = this.cachedCamera.get('position');
		const direction = new Vector3().subVectors(position,target).normalize();
		const spherical = new Spherical().setFromVector3(direction);
		
		const delta = calculateDeltaFromVec3(position,target);
		const currentDelta = controls.getAzimuthalAngle();
		const currentPolarAngle = controls.getPolarAngle();
		const thetaLerp = new Lerp(currentDelta,currentDelta + delta);
		const distanceLerp = new Lerp(trackballControls.minDistance,position.distanceTo(target));
		const phiLerp = new Lerp(currentPolarAngle,spherical.phi);
		
		transition.scale = 0;
		transition.opacity = 1;
		transition.active = true;
		
		gsap.to(controls.target,{
			x: target.x,
			y: target.y,
			z: target.z,
			duration: GSAPAnimationMixer.duration,
			ease: GSAPAnimationMixer.ease
		});
		
		const cameraProgress = {
			value: 0
		};
		
		const dir = new Vector3();
		
		gsap.to(cameraProgress,{
			value: 1,
			duration: GSAPAnimationMixer.duration,
			ease: GSAPAnimationMixer.ease,
			onUpdate: () => {
				if (cameraProgress.value < 0.5) {
					transition.opacity = clamp(1 - cameraProgress.value,0.5,1);
				} else {
					transition.opacity = clamp(cameraProgress.value,0.5,1);
				}
				controls.setAzimuthAngle(thetaLerp.lerp(cameraProgress.value));
				controls.setPolarAngle(phiLerp.lerp(cameraProgress.value));
				dir.subVectors(camera.position,controls.target);
				dir.normalize().setLength(distanceLerp.lerp(cameraProgress.value)).add(controls.target);
				camera.position.copy(dir);
			},
			onComplete: () => {
				transition.active = false;
				CameraCollision.prevent = false;
				this.restoreControlsSettings();
				
				config.threejs.label.resize = this.cachedLabels.get('resize');
			}
		});
		
		if (OrbitDayAndNight.isActiveAndSelected && this.currentTimeCache) {
			const time = {value: Number(OrbitDayAndNight.scope.range.element.value)};
			
			gsap.to(time,{
				value: this.currentTimeCache,
				ease: GSAPAnimationMixer.ease,
				duration: GSAPAnimationMixer.duration,
				onUpdate: () => {
					OrbitDayAndNight.scope.range.element.value = time.value;
					OrbitDayAndNight.scope.updateTimesOfDay(time.value, true);
				}
			});
		}
		
	}
	
}
